import * as React from 'react';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@opendoor/bricks/core';
import { media } from '@opendoor/bricks/theme/novo';

const outAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    height: 0;
  }
`;

const inAnimation = keyframes`
  from {
    top: 20px;
    opacity: 0;
  }
  to {
    top: 0px;
    opacity: 1;
  }
`;

const gapAnimation = keyframes`
  from {
    gap: 26px 16px;
  }
  to {
    gap: 16px;
  }
`;

const imageSelectGapAnimation = keyframes`
  from {
    gap: 22px 12px;
  }
  to {
    gap: 12px;
  }
`;

const fieldsetGapAnimation = keyframes`
  from {
    gap: 78px 48px;
  }
  to {
    gap: 48px;
  }
`;

const OutContainer = styled.div`
  animation-name: ${outAnimation};
  animation-duration: 0.512s;
  animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  animation-fill-mode: forwards;
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
`;

const inDelay = '0.32s';
const inDuration = '0.64s';
export const InContainer = styled.div`
  opacity: 0;
  position: relative;

  animation-name: ${inAnimation};
  animation-delay: ${inDelay};
  animation-duration: ${inDuration};
  animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  animation-fill-mode: forwards;

  div[role='radiogroup'],
  div[role='checkboxgroup'],
  div[role='radiogroup'].image-select,
  fieldset {
    animation-name: ${gapAnimation};
    animation-delay: ${inDelay};
    animation-duration: ${inDuration};
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
    animation-fill-mode: forwards;
  }

  fieldset {
    animation-name: ${fieldsetGapAnimation};
  }

  ${media.smallerThan.LG} {
    div[role='radiogroup'].image-select {
      animation-name: ${imageSelectGapAnimation};
    }
  }

  @media (prefers-reduced-motion) {
    opacity: 1;
    animation-name: none;
    div[role='radiogroup'],
    div[role='checkboxgroup'],
    div[role='radiogroup'].image-select,
    fieldset {
      animation-name: none;
    }
  }
`;

type CrossFadeAnimationProps = {
  /**
   * When this value changes the CrossFadeAnimation component will re-run the animation
   */
  watchValue: string;
  children: React.ReactNode;
};

export const CrossFadeAnimation: React.FC<CrossFadeAnimationProps> = ({ watchValue, children }) => {
  const [prevProps, setPrevProps] = React.useState<any>({ watchValue: null, children: null });

  // see if the user prefers reduced motion, if so we will not animate
  const { matches: prefersReducedMotion } = window
    ? window.matchMedia('(prefers-reduced-motion)')
    : { matches: true };

  // save our current set of props, we set a timeout that ends after the
  // animation so we can remove the old children and store our current props
  // as our previous props for the next change
  React.useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    const timeout = setTimeout(() => {
      setPrevProps({ watchValue, children });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [children, watchValue, setPrevProps, prefersReducedMotion]);

  if (
    !prefersReducedMotion &&
    prevProps.watchValue !== null &&
    prevProps.watchValue !== watchValue
  ) {
    return (
      <Box position="relative">
        <OutContainer aria-hidden="true">{prevProps.children}</OutContainer>
        <InContainer>{children}</InContainer>
      </Box>
    );
  }

  //eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
